import { Rectangle, Texture } from 'pixi.js';
import type { KnyttWorld } from '../engine/KnyttWorld';
import {
  TILE_SIZE,
  TILES_PER_SET,
  TILESET_COLS,
  TILESET_ROWS,
  Tileset,
} from './Tileset';

/**
 * A path genuinely missing from both the bin archive and the static data dir
 * must resolve to `null`, not fabricated bytes. The dev server (and some
 * static hosts, via an SPA catch-all rewrite) answer an unmatched `fetch()`
 * with 200 + `index.html` instead of a real 404 — none of our assets
 * (png/ogg/wav/ini/bin) are ever `text/html`, so treat that as "missing".
 */
export function isFakeNotFoundResponse(resp: Response): boolean {
  return (resp.headers.get('content-type') ?? '').startsWith('text/html');
}

/**
 * Loads and caches a world's visual assets (tilesets, gradients, sprite sheets),
 * resolving each file from the .knytt.bin archive first, then bundled system data.
 */
export class AssetManager {
  private tilesets = new Map<number, Promise<Tileset | null>>();
  private gradients = new Map<number, Promise<Texture | null>>();
  private textures = new Map<string, Promise<Texture | null>>();

  constructor(
    private readonly world: KnyttWorld,
    private readonly dataBaseUrl: string,
  ) {}

  /** Resolve raw bytes (bin first, then fetch system). */
  async resolveBytes(path: string): Promise<Uint8Array | null> {
    const bytes = this.world.getFileBytes(path);
    if (bytes) return bytes;
    try {
      const resp = await fetch(`${this.dataBaseUrl}${path}`);
      if (!resp.ok || isFakeNotFoundResponse(resp)) return null;
      return new Uint8Array(await resp.arrayBuffer());
    } catch {
      return null;
    }
  }

  private async loadBitmap(path: string): Promise<ImageBitmap | null> {
    const bytes = this.world.getFileBytes(path);
    let blob: Blob | null = null;
    if (bytes) {
      blob = new Blob([bytes as BlobPart], { type: 'image/png' });
    } else {
      try {
        const resp = await fetch(`${this.dataBaseUrl}${path}`);
        if (!resp.ok || isFakeNotFoundResponse(resp)) return null;
        blob = await resp.blob();
      } catch {
        return null;
      }
    }
    try {
      return await createImageBitmap(blob);
    } catch {
      return null;
    }
  }

  getTileset(num: number): Promise<Tileset | null> {
    let p = this.tilesets.get(num);
    if (!p) {
      p = this.buildTileset(num);
      this.tilesets.set(num, p);
    }
    return p;
  }

  private async buildTileset(num: number): Promise<Tileset | null> {
    const bitmap = await this.loadBitmap(`Tilesets/Tileset${num}.png`);
    if (!bitmap) return null;

    const w = TILESET_COLS * TILE_SIZE; // 384
    const h = TILESET_ROWS * TILE_SIZE; // 192
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;

    // Detect alpha; if fully opaque, treat magenta (255,0,255) as transparent.
    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        hasAlpha = true;
        break;
      }
    }
    if (!hasAlpha) {
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] === 255 && data[i + 1] === 0 && data[i + 2] === 255) {
          data[i + 3] = 0;
        }
      }
      ctx.putImageData(img, 0, 0);
    }

    // Build masks from (possibly modified) alpha.
    const masks: Uint8Array[] = [];
    for (let t = 0; t < TILES_PER_SET; t++) {
      const tx = (t % TILESET_COLS) * TILE_SIZE;
      const ty = Math.floor(t / TILESET_COLS) * TILE_SIZE;
      const mask = new Uint8Array(TILE_SIZE * TILE_SIZE);
      for (let ly = 0; ly < TILE_SIZE; ly++) {
        for (let lx = 0; lx < TILE_SIZE; lx++) {
          const px = tx + lx;
          const py = ty + ly;
          const a = data[(py * w + px) * 4 + 3];
          mask[ly * TILE_SIZE + lx] = a > 0 ? 1 : 0;
        }
      }
      masks.push(mask);
    }

    // Build a Pixi texture from the processed canvas.
    const base = Texture.from(canvas);
    const source = base.source;
    source.scaleMode = 'nearest';

    const frames: Texture[] = [];
    for (let t = 0; t < TILES_PER_SET; t++) {
      const tx = (t % TILESET_COLS) * TILE_SIZE;
      const ty = Math.floor(t / TILESET_COLS) * TILE_SIZE;
      frames.push(
        new Texture({
          source,
          frame: new Rectangle(tx, ty, TILE_SIZE, TILE_SIZE),
        }),
      );
    }

    return new Tileset(base, frames, masks);
  }

  getGradient(num: number): Promise<Texture | null> {
    let p = this.gradients.get(num);
    if (!p) {
      p = this.buildGradient(num);
      this.gradients.set(num, p);
    }
    return p;
  }

  private async buildGradient(num: number): Promise<Texture | null> {
    const bitmap = await this.loadBitmap(`Gradients/Gradient${num}.png`);
    if (!bitmap) return null;
    const tex = Texture.from(bitmap);
    tex.source.scaleMode = 'nearest';
    tex.source.addressMode = 'repeat';
    return tex;
  }

  /** Generic texture loader for sprite sheets (objects, juni, etc.). */
  getTexture(path: string): Promise<Texture | null> {
    let p = this.textures.get(path);
    if (!p) {
      p = this.buildTexture(path);
      this.textures.set(path, p);
    }
    return p;
  }

  private async buildTexture(path: string): Promise<Texture | null> {
    const bitmap = await this.loadBitmap(path);
    if (!bitmap) return null;

    // Inspect alpha; if the sheet is fully opaque it uses the magenta (255,0,255)
    // colour key (Knytt Stories convention) which we convert to transparent.
    const w = bitmap.width;
    const h = bitmap.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        hasAlpha = true;
        break;
      }
    }
    if (!hasAlpha) {
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] === 255 && data[i + 1] === 0 && data[i + 2] === 255) data[i + 3] = 0;
      }
      ctx.putImageData(img, 0, 0);
    }

    const tex = Texture.from(canvas);
    tex.source.scaleMode = 'nearest';
    return tex;
  }
}
